import React from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Image } from
'@chakra-ui/react';
import {
  RefreshCcw,
  XCircle,
  AlertTriangle,
  Search,
  Shield,
  EyeOff,
  Activity } from
'lucide-react';
const MotionBox = motion(Box);
export function ProblemSection() {
  return (
    <Box
      as="section"
      py={20}
      bg="brand.card"
      borderY="1px solid"
      borderColor="brand.border"
      position="relative"
      overflow="hidden">
      
      <Container maxW="container.xl" px={4}>
        {/* Pain Agitation */}
        <Box maxW="4xl" mx="auto" mb={32}>
          <MotionBox
            initial={{
              opacity: 0,
              y: 20
            }}
            whileInView={{
              opacity: 1,
              y: 0
            }}
            viewport={{
              once: true
            }}
            textAlign="center"
            mb={12}>
            
            <Heading
              as="h2"
              fontSize={{
                base: '3xl',
                md: '4xl'
              }}
              fontWeight="bold"
              mb={6}>
              
              Most store owners don't fail once.{' '}
              <br
                style={{
                  display: 'none'
                }} />
              
              <Text as="span" color="red.400">
                They get stuck in a loop:
              </Text>
            </Heading>
          </MotionBox>

          <Box position="relative">
            {/* Connecting line for desktop */}
            <Box
              display={{
                base: 'none',
                md: 'block'
              }}
              position="absolute"
              top="50%"
              left={0}
              w="full"
              h="2px"
              bg="brand.border"
              transform="translateY(-50%)"
              zIndex={0} />
            

            <SimpleGrid
              columns={{
                base: 1,
                md: 4
              }}
              spacing={6}
              position="relative"
              zIndex={10}>
              
              {[
              {
                step: 1,
                title: 'Appeal',
                icon: RefreshCcw,
                color: 'blue.400',
                bg: 'rgba(96, 165, 250, 0.1)',
                borderColor: 'rgba(96, 165, 250, 0.2)'
              },
              {
                step: 2,
                title: 'Rejected',
                icon: XCircle,
                color: 'red.400',
                bg: 'rgba(248, 113, 113, 0.1)',
                borderColor: 'rgba(248, 113, 113, 0.2)'
              },
              {
                step: 3,
                title: 'Fix random things',
                icon: AlertTriangle,
                color: 'yellow.400',
                bg: 'rgba(250, 204, 21, 0.1)',
                borderColor: 'rgba(250, 204, 21, 0.2)'
              },
              {
                step: 4,
                title: 'Rejected again',
                icon: XCircle,
                color: 'red.500',
                bg: 'rgba(239, 68, 68, 0.1)',
                borderColor: 'rgba(239, 68, 68, 0.2)'
              }].
              map((item, i) =>
              <MotionBox
                key={i}
                initial={{
                  opacity: 0,
                  y: 20
                }}
                whileInView={{
                  opacity: 1,
                  y: 0
                }}
                viewport={{
                  once: true
                }}
                transition={{
                  delay: i * 0.1
                }}
                bg="brand.dark"
                border="1px solid"
                borderColor={item.borderColor}
                borderRadius="xl"
                p={6}
                textAlign="center"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                position="relative">
                
                  <Flex
                  w={12}
                  h={12}
                  borderRadius="full"
                  bg={item.bg}
                  color={item.color}
                  align="center"
                  justify="center"
                  mb={4}>
                  
                    <item.icon size={24} />
                  </Flex>
                  <Text fontWeight="semibold" color="gray.200">
                    {item.title}
                  </Text>
                  {i < 3 &&
                <Box
                  display={{
                    base: 'block',
                    md: 'none'
                  }}
                  position="absolute"
                  bottom={-6}
                  color="brand.border">
                  
                      ↓
                    </Box>
                }
                </MotionBox>
              )}
            </SimpleGrid>
          </Box>

          <MotionBox
            initial={{
              opacity: 0,
              y: 20
            }}
            whileInView={{
              opacity: 1,
              y: 0
            }}
            viewport={{
              once: true
            }}
            mt={12}
            textAlign="center"
            bg="rgba(239, 68, 68, 0.05)"
            border="1px solid"
            borderColor="rgba(239, 68, 68, 0.1)"
            borderRadius="xl"
            p={8}>
            
            <Text fontSize="xl" fontWeight="medium" color="gray.300" mb={4}>
              Every failed appeal trains Google to trust you less.
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="white">
              And the worst part?{' '}
              <Text as="span" color="red.400">
                Google doesn't tell you what's actually wrong.
              </Text>
            </Text>
          </MotionBox>
        </Box>

        {/* Unique Mechanism */}
        <Box maxW="6xl" mx="auto">
          <SimpleGrid
            columns={{
              base: 1,
              lg: 2
            }}
            spacing={16}
            alignItems="center">
            
            <MotionBox
              initial={{
                opacity: 0,
                x: -20
              }}
              whileInView={{
                opacity: 1,
                x: 0
              }}
              viewport={{
                once: true
              }}>
              
              <Heading
                as="h2"
                fontSize={{
                  base: '3xl',
                  md: '4xl'
                }}
                fontWeight="bold"
                mb={6}>
                
                We don't guess. We simulate how Google evaluates your store.
              </Heading>
              <Text color="gray.400" fontSize="lg" mb={8}>
                Our system scans your entire digital footprint exactly like
                Google's automated reviewers do.
              </Text>

              <Box display="flex" flexDirection="column" gap={6}>
                {[
                {
                  icon: Shield,
                  title: 'Trust signals on and off your website'
                },
                {
                  icon: Search,
                  title: 'Policy risks Google checks outside Merchant Center'
                },
                {
                  icon: EyeOff,
                  title: 'Hidden inconsistencies that trigger rejections'
                },
                {
                  icon: Activity,
                  title: 'Reputation + entity signals tied to your business'
                }].
                map((feature, i) =>
                <Flex key={i} align="flex-start" gap={4}>
                    <Flex
                    w={10}
                    h={10}
                    borderRadius="lg"
                    bg="rgba(16, 185, 129, 0.1)"
                    border="1px solid"
                    borderColor="rgba(16, 185, 129, 0.2)"
                    align="center"
                    justify="center"
                    flexShrink={0}
                    mt={1}>
                    
                      <feature.icon color="#10b981" size={20} />
                    </Flex>
                    <Box>
                      <Text fontSize="lg" fontWeight="medium" color="gray.200">
                        {feature.title}
                      </Text>
                    </Box>
                  </Flex>
                )}
              </Box>

              <Flex
                mt={10}
                display="inline-flex"
                align="center"
                gap={3}
                px={5}
                py={3}
                borderRadius="lg"
                bg="brand.dark"
                border="1px solid"
                borderColor="brand.border">
                
                <Box
                  w={2}
                  h={2}
                  borderRadius="full"
                  bg="brand.green"
                  className="animate-pulse" />
                
                <Text fontWeight="medium" color="brand.light">
                  Over 200+ automated checks — in under a minute.
                </Text>
              </Flex>
            </MotionBox>

            <MotionBox
              initial={{
                opacity: 0,
                x: 20
              }}
              whileInView={{
                opacity: 1,
                x: 0
              }}
              viewport={{
                once: true
              }}
              position="relative">
              
              <Box
                position="absolute"
                inset={0}
                bg="rgba(16, 185, 129, 0.1)"
                filter="blur(100px)"
                borderRadius="full"
                pointerEvents="none" />
              
              <Box
                position="relative"
                borderRadius="xl"
                border="1px solid"
                borderColor="rgba(16, 185, 129, 0.3)"
                bg="brand.dark"
                p={2}
                boxShadow="glow">
                
                {/* <Image
                  src="/sample-report-1.png"
                  alt="GMCRadar Sample Report"
                  w="full"
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="rgba(30, 58, 40, 0.5)" /> */}
                
              </Box>
            </MotionBox>
          </SimpleGrid>
        </Box>
      </Container>
    </Box>);

}
